import React, { useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import uniqid from 'uniqid';
import validator from 'validator';

// AWS UI Components
import {
    Alert,
    Autosuggest,
    Button,
    Container,
    DatePicker,
    Flashbar,
    FlashbarProps,
    FormField,
    Header,
    Input,
    Popover,
    Select,
    SpaceBetween
} from '@amzn/awsui-components-react';

// TipTap Editor
import TextEditor from '../../../tiptap/TipTap';

// Common
import { useContinuousAuth } from '../../../pages/App';
import { useEmployeeData, UserRole } from '../../../common/EmployeeContext';
import { isValidUrl } from '../../../common/ValidationUtils';
import settings from '../../../settings.json';

// Content
import content from '../coaching-catalog';

// Styles
import Styled from './styled';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(customParseFormat);

type CoachingFormProps = {
    menuClick: (title: string, path: string, param: string) => void;
    user: string;
    role?: UserRole;
    userPermissions: Set<string>;
};

type CoachingReason = {
    value: string;
    action?: boolean;
};

const settingEnv = (settings as any)[window.location.host] || settings.default;
const { api_prefix: apiPrefix } = settingEnv;

export const coachingActions = [
    { label: 'No action required', value: 'No action required' },
    { label: 'Counsel', value: 'Counsel' },
    { label: 'First Written', value: 'First Written' },
    { label: 'Second Written', value: 'Second Written' },
];
export const hrActions = [
    { label: 'Final Written', value: 'Final Written' },
    { label: 'Separation', value: 'Separation' },
];

const currentDate = dayjs().utc().format('YYYY-MM-DD');
const dueDate = dayjs().add(5, 'days').format('YYYY-MM-DD');
const defaultEditorValue = '<p> </p>';

// Tooltips / Popovers (similar to your old code)
const additionalNotesWarning = (
    <div>
        <p>
            When entering notes from your coaching with an associate, users are expected to follow the Do’s and Don’ts Best Practices.<br /><br />
            Please review these guidelines before recording documentation in Coaching Corner.
        </p>
        <Button
            variant="primary"
            href="https://ring.shelf.io/read/00146c14-a240-478a-aa5c-c72661679bf9"
            target="_blank"
        >
            Best Practices
        </Button>
    </div>
);

const hrReminder = (
    <div>
        <p>
            <b>Reminder:</b> <br />Please use the corrective action template for formal coaching documentation.
        </p>
        <Button
            variant="primary"
            href="https://central.ring.com/support_article/ring-community-support-l1-to-l3-performance-management/"
            target="_blank"
        >
            Corrective Action Template
        </Button>
    </div>
);

const attachmentReminder = (
    <div>
        <p style={{ marginBottom: '5px' }}>
            <b>Reminder:</b> <br />
            Any attachments uploaded in this section will be viewable by the coached employee.
        </p>
    </div>
);

const CoachingForm: React.FC<CoachingFormProps> = (props) => {
    const { menuClick, role } = props;

    const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);
    const [loading, setLoading] = useState(false);

    // Main Fields
    const [agent, setAgent] = useState('');
    const [supervisor, setSupervisor] = useState<string | undefined>('');
    const [location, setLocation] = useState<string | undefined>('');
    const [kustomerUrl, setKustomerUrl] = useState('');
    const [date, setDate] = useState('');
    const [notes, setNotes] = useState(defaultEditorValue);
    const [additionalNotes, setAdditionalNotes] = useState(defaultEditorValue);

    // Category/Reason/Action
    const [category, setCategory] = useState('');
    const [reason, setReason] = useState('');
    const [action, setAction] = useState('');
    const [goodStanding, setGoodStanding] = useState(true);
    const [hrReview, setHrReview] = useState(false);
    const [coachingStatus, setCoachingStatus] = useState('');

    // S3 Attachments
    const [attachmentBody, setAttachmentBody] = useState<File | undefined>(undefined);

    // UX
    const [disabled, setDisabled] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [requestFailed, setRequestFailed] = useState<boolean>(false);

    const { getHeaders } = useContinuousAuth();

    const {
        agents,
        leads,
        supervisors,
        loading: employeesLoading,
        error: employeesError,
    } = useEmployeeData();

    const associatesList = useMemo(() => {
        return [...agents, ...leads]; 
      }, [agents, leads]);

    const setTmDetails = useCallback((selectedAgent: { label: string; value: string }) => {
        const found = associatesList.find(h => h.Username === selectedAgent.value);
        if (found) {
            setAgent(found.Username);
            setSupervisor(found.Supervisor);
            setLocation(found.Site);
        }
    }, [associatesList]);

    const handleDateChange = useCallback((e: any) => {
        const dateValue = e.detail.value;
        if (dateValue) {
            const formatted = dayjs(dateValue, 'YYYY/MM/DD').format('YYYY-MM-DD');
            setDate(formatted);
        } else {
            setDate('');
        }
    }, []);

    const setActionDetails = useCallback((selectedAction: { label: string; value: string }) => {
        const value = selectedAction.value;
        setAction(value);

        if (coachingActions.some(action => action.value === value)) {
            setGoodStanding(true);
            setHrReview(false);
            setCoachingStatus('Delivered');
        }

        // Check if the chosen value is among the "hrActions" array
        if (hrActions.some(action => action.value === value)) {
            setGoodStanding(false);
            setHrReview(true);
            setCoachingStatus('Pending');
        }
    }, []);

    const inputValidationCheck = useCallback((): string[] => {
        const errors: string[] = [];

        if (!agent) {
            errors.push('Please Provide Agent Associated');
        }
        if (!category) {
            errors.push('Please Provide Category Associated');
        }
        if (!reason) {
            errors.push('Please Provide Reason Associated');
        }
        if (!action) {
            errors.push('Please Provide Action Associated');
        }
        if (!date || !validator.isDate(date)) {
            errors.push('Invalid Date');
        }
        if (kustomerUrl && !isValidUrl(kustomerUrl)) {
            errors.push(
                'Invalid SalesForce URL. It must start with "https://" and have a valid salesforce domain'
            );
        }
        if (!notes || notes === defaultEditorValue) {
            errors.push('Notes Field Cannot be Empty');
        }
        // Supervisor fallback check
        if (!validator.isEmail(supervisor ? supervisor : 'unassignedSupervisor@ring.com')) {
            // fallback
            setSupervisor('unassignedSupervisor@ring.com');
        }

        return errors;
    }, [agent, category, reason, action, date, kustomerUrl, notes, supervisor]);

    const attachmentFetch = useCallback(async (file: File) => {
        setLoading(true);
        let storedKey = '';
        try {
            const headers = await getHeaders();
            // request presigned URL
            const payload = { uid: uniqid(), type: file.type };
            const response = await axios.post(apiPrefix + '/api/coaching-attachment', payload, { headers });
            const { presignedPost, key } = response.data;
            const { url, fields } = presignedPost;

            // POST file to S3
            const formData = new FormData();
            Object.entries(fields).forEach(([k, v]) => {
                formData.append(k, v as string);
            });
            formData.append('file', file);
            await axios.post(url, formData);

            storedKey = key;
            return { key: storedKey };
        } catch (e: any) {
            console.error('Attachment Upload Error', e.message);
            showErrorFlashbar('There was a problem uploading your file. File type may not be supported.');
            throw e;
        } finally {
            setLoading(false);
        }
    }, [getHeaders]);

    const handleSubmit = useCallback(async () => {
        setSubmitted(false);
        setRequestFailed(false);
        setDisabled(true);
        setLoading(true);
        setFlashbarItems([]);

        // Validate
        const errors = inputValidationCheck();
        if (errors.length > 0) {
            showFormErrors(errors);
            setDisabled(false);
            setLoading(false);
            return;
        }

        // Upload attachment if present
        let fileObj = {};
        if (attachmentBody) {
            try {
                fileObj = await attachmentFetch(attachmentBody);
            } catch (e) {
                // If attachment fails, we'll just stop
                setDisabled(false);
                setLoading(false);
                return;
            }
        }

        // Construct payload
        const formId = uniqid();
        const requestPayload = {
            action,
            agent,
            additionalNotes: additionalNotes?.toString() || defaultEditorValue,
            category,
            createdDate: currentDate,
            date,
            deleted: false,
            dueDate,
            file: fileObj,
            formId,
            goodStanding,
            hrReview,
            kustomerUrl: kustomerUrl ? kustomerUrl : 'N/A',
            location,
            notes: notes?.toString() || defaultEditorValue,
            reason,
            status: coachingStatus,
            supervisor
        };

        // API Post
        try {
            const headers = await getHeaders();
            await axios.put(apiPrefix + '/api/coaching-tool', requestPayload, { headers });

            // On success
            setSubmitted(true);
            setFlashbarItems([
                {
                    type: 'success',
                    content: 'Thank you for your submission. A copy of your completed coaching has been delivered via email.',
                    dismissible: false,
                    id: 'successful-submission',
                    action: (
                        <Button
                            variant='normal'
                            onClick={() => menuClick('Coaching Dashboard', 'coaching', '')}
                        >
                            Go to Coaching Dashboard
                        </Button>
                    )
                }
            ]);
        } catch (e: any) {
            console.error('Coaching Form Submission Error:', e.message);
            setRequestFailed(true);
            const errorMessage =
                e?.response?.data?.message || 'There was a problem with your Request.';
            showErrorFlashbar(errorMessage);
            setDisabled(false);
        } finally {
            setLoading(false);
        }
    }, [
        action,
        agent,
        additionalNotes,
        category,
        date,
        coachingStatus,
        goodStanding,
        hrReview,
        kustomerUrl,
        location,
        notes,
        reason,
        supervisor,
        attachmentBody,
        inputValidationCheck,
        menuClick,
        attachmentFetch,
        getHeaders
    ]);

    const showErrorFlashbar = (messageText: string) => {
        setFlashbarItems([{
            type: 'error',
            content: messageText,
            dismissible: true,
            onDismiss: () => setFlashbarItems([]),
            id: `error-${uniqid()}`
        }]);
    };

    const showFormErrors = (errors: string[]) => {
        setFlashbarItems([{
            type: 'error',
            content: (
                <>
                    Please resolve the fields listed below:
                    {errors.map((err, i) => (
                        <div key={i}>- {err}</div>
                    ))}
                </>
            ),
            dismissible: true,
            onDismiss: () => setFlashbarItems([]),
            id: `error-${uniqid()}`
        }]);
    };

    // Hierarchy dropdown for agent
    const hierarchyOptions = useMemo(() => {
        return associatesList?.map(h => ({
            label: h.Username,
            value: h.Username
        }));
    }, [associatesList]);

    // Category list
    const categoryOptions = useMemo(() => {
        if (role === UserRole.Lead) {
            return [{ label: 'Coaching', value: 'Coaching' }];
        }
        return content.map(c => ({ label: c.Category, value: c.Category }));
    }, [role]);

    // Reasons for selected category
    const reasonOptions = useMemo(() => {
        if (!category) return [];
        const foundCategory = content.find(c => c.Category === category);
        if (!foundCategory) return [];
        return foundCategory.reasons.map((r) => ({ label: r.value, value: r.value }));
    }, [category]);

    // If user picks a reason, we find whether it has action===true. If so, show the hr + standard action groups
    const hrMetadata = useMemo(() => {
        if (!category || !reason) return undefined;
        const foundCategory = content.find(c => c.Category === category);
        if (!foundCategory) return undefined;
        const foundReason = foundCategory.reasons.find((r: CoachingReason) => r.value === reason);
        return foundReason;
    }, [category, reason]);

    // Action dropdown
    const actionOptions = useMemo(() => {
        if (hrMetadata?.action) {
            // Return "no HR involvement" group and "HR will be included" group
            return [
                { label: 'No action required', value: 'No action required' },
                { label: 'Counsel', value: 'Counsel' },
                { label: 'First Written', value: 'First Written' },
                { label: 'Second Written', value: 'Second Written' },
                { label: 'Final Written', value: 'Final Written' },
                { label: 'Separation', value: 'Separation' }
            ].map(a => ({ label: a.label, value: a.value }));
        }
        // Otherwise only "No action required"
        return [{ label: 'No action required', value: 'No action required' }];
    }, [hrMetadata]);

    return (
        <Styled.FormBody>
            {flashbarItems.length > 0 && (
                <Styled.Banner>
                    <Flashbar items={flashbarItems} />
                </Styled.Banner>
            )}
            <Container
                header={<Header variant="h2">User Details</Header>}
            >
                <SpaceBetween direction="horizontal" size="m">
                    {/* Agent */}
                    <FormField label="Agent">
                        <Autosuggest
                            value={agent}
                            onChange={e => setAgent(e.detail.value)}
                            onSelect={e => {
                                const selectedValue = e.detail.value;
                                const found = associatesList.find(h => h.Username === selectedValue);
                                if (found) {
                                    setTmDetails({
                                        label: found.Username,
                                        value: found.Username
                                    });
                                }
                            }}
                            options={hierarchyOptions}
                            placeholder='Agent Associated'
                        />
                    </FormField>

                    {/* Supervisor (disabled) */}
                    <FormField label="Supervisor">
                        <Select
                            selectedOption={
                                supervisor ? { value: supervisor, label: supervisor } : null
                            }
                            disabled
                            options={[]}
                            placeholder="Supervisor Associated"
                        />
                    </FormField>

                    {/* Salesforce URL */}
                    <FormField label="SalesForce URL">
                        <Input
                            value={kustomerUrl}
                            onChange={e => setKustomerUrl(e.detail.value)}
                            placeholder="optional"
                        />
                    </FormField>

                    {/* Date */}
                    <FormField label="Date">
                        <DatePicker
                            onChange={handleDateChange}
                            value={date ? dayjs(date, 'YYYY-MM-DD').format('YYYY/MM/DD') : ''}
                            placeholder="Select Date"
                        />
                    </FormField>
                </SpaceBetween>
            </Container>
            <Styled.FormDetails>
                {/* Notes */}
                <Container
                    header={<Header variant="h2">Notes / Comments</Header>}
                    className='review-details'
                >
                    <SpaceBetween direction="vertical" size="m">
                        <FormField label="Notes (Visible to Agent)" stretch={true}>
                            <TextEditor
                                value={notes}
                                onChange={(value: string) => setNotes(value)}
                            />
                        </FormField>

                        <FormField
                            label={
                                <Popover
                                    dismissButton={false}
                                    size="large"
                                    position="top"
                                    triggerType='text'
                                    content={additionalNotesWarning}
                                >
                                    Additional Notes (Supervisor/HR)
                                </Popover>
                            }
                            stretch={true}
                        >
                            <TextEditor
                                value={additionalNotes}
                                onChange={(value: string) => setAdditionalNotes(value)}
                            />
                        </FormField>
                    </SpaceBetween>
                </Container>

                {/* Coaching Details */}
                <Container
                    header={<Header variant="h2">Coaching Details</Header>}
                    className='side-panel'
                >
                    <SpaceBetween size="m">
                        {/* Category */}
                        <FormField label="Category">
                            <Select
                                selectedOption={
                                    category ? { label: category, value: category } : null
                                }
                                onChange={e => {
                                    setCategory(e.detail.selectedOption.value || '');
                                    setReason('');
                                    setAction('');
                                }}
                                options={categoryOptions}
                                placeholder="Select Category"
                            />
                        </FormField>

                        {/* Reason */}
                        <FormField label="Reason">
                            <Select
                                selectedOption={
                                    reason ? { label: reason, value: reason } : null
                                }
                                onChange={e => {
                                    const val = e.detail.selectedOption.value;
                                    setReason(val || '');
                                    setAction('');
                                }}
                                options={reasonOptions}
                                placeholder="Select Reason"
                            />
                        </FormField>

                        {/* Action */}
                        <FormField label="Action">
                            <Select
                                selectedOption={
                                    action ? { label: action, value: action } : null
                                }
                                onChange={e => {
                                    const selected = e.detail.selectedOption;
                                    if (selected?.value) {
                                        setActionDetails({ label: selected.label ?? '', value: selected.value });
                                    }
                                }}
                                options={actionOptions}
                                placeholder="Select Action"
                            />
                        </FormField>

                        {/* If hrReview is set to TRUE, show hrReminder */}
                        {hrMetadata?.action === true && hrReview && (
                            <Alert type="info">
                                {hrReminder}
                            </Alert>
                        )}

                        {/* File attachment */}
                        <FormField label="Attachment (optional)">
                            <input
                                type="file"
                                id="fileInput"
                                accept=".jpg, .jpeg, .png, .csv, .doc, .docx, .xls, .xlsx, .pdf"
                                onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                        setAttachmentBody(e.target.files[0]);
                                    } else {
                                        setAttachmentBody(undefined);
                                    }
                                }}
                            />
                            <div style={{ marginTop: '6px', fontSize: '12px' }}>
                                <strong>Max File Size:</strong> 5MB<br />
                                <strong>Supported File Types:</strong> jpg, png, csv, doc, docx, xls, xlsx, pdf
                            </div>
                            {category === 'Coaching' && (
                                <Alert type="info">
                                    {attachmentReminder}
                                </Alert>
                            )}
                        </FormField>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            loading={loading || employeesLoading}
                            disabled={disabled || loading}
                        >
                            {
                                loading ? 'Loading...' :
                                    requestFailed ? 'Re-Submit' :
                                        submitted ? 'Success!' : 'Submit'
                            }
                        </Button>
                    </SpaceBetween>
                </Container>
            </Styled.FormDetails>
        </Styled.FormBody>
    );
};

export default CoachingForm;
